import React from 'react';
import {
  Alert, Button, EmptyState, ErrorState, SegmentedControl, Table, TextInput,
} from '@parsina/framework';
import { Banknote, SearchX, X } from 'lucide-react';

import { useTranslation, toAsciiDigits } from '../../i18n';
import { SEARCH_DEBOUNCE_MS } from '../../constants/list';
import { CLOSE_SETTLEMENT, MARK_SETTLEMENT_PAID, SETTLEMENTS_QUERY } from './queries';
import {
  ActionConfirm, Code, Fact, LoadMore, MarketDrawer, MoneyCell, NotInList, StatusChip, TableSkeleton,
  allIrr, isIrr, useEnumLabel, usePagedList,
} from './shared';

export const SETTLEMENT_STATUSES = ['', 'open', 'closed', 'paid'];
/** A Jalali month as the service keys it: "1405/06". */
export const PERIOD = /^\d{4}\/\d{2}$/;

const pickSettlements = (d) => d?.marketplaceSettlementsAdmin;

/**
 * View «تسویه‌ها» — open → close → mark paid (design §5.7, §13).
 * `?sstatus=` and `?period=` filter; `?settlement=<id>` opens the drawer.
 */
export default function SettlementsView({ view, go }) {
  const { t, formatDigits } = useTranslation();
  const period = PERIOD.test(view.period) ? view.period : '';
  const list = usePagedList(SETTLEMENTS_QUERY, pickSettlements, { status: view.sstatus || null, period: period || null });
  const selected = view.settlement ? list.rows.find((r) => r.id === view.settlement) : null;
  const filtered = !!(view.sstatus || period);

  // The month field is typed; it narrows once it is a whole "yyyy/mm".
  const [draft, setDraft] = React.useState(view.period ? formatDigits(view.period) : '');
  const ascii = toAsciiDigits(draft).trim();
  const draftInvalid = !!ascii && !PERIOD.test(ascii);
  React.useEffect(() => {
    if (ascii === (view.period || '') || draftInvalid) return undefined;
    const timer = setTimeout(() => go({ period: ascii, settlement: '' }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [ascii, draftInvalid, view.period, go]);

  const clear = () => { setDraft(''); go({ sstatus: '', period: '', settlement: '' }); };

  // Settlements of different stores may be in different currencies. An all-IRR
  // page is exactly what it was; otherwise the headers name no unit and every
  // figure carries its own.
  const unitPerFigure = !allIrr(list.rows);
  const amountHeader = (key) => (unitPerFigure
    ? t(`marketplace.settlements.columnsNoUnit.${key}`)
    : t(`marketplace.settlements.columns.${key}`));
  const amount = (r, field, className) => (
    <MoneyCell rials={r[field]} currency={r.currency} withUnit={unitPerFigure} className={className} />
  );

  const columns = [
    {
      key: 'vendor',
      header: t('marketplace.settlements.columns.vendor'),
      render: (r) => (r.vendorName ? <span dir="auto" className="font-semibold text-foreground">{r.vendorName}</span> : <span className="text-muted-foreground">{t('marketplace.listings.unknownVendor')}</span>),
    },
    { key: 'period', header: t('marketplace.settlements.columns.period'), width: '6rem', render: (r) => <span className="tabular-nums">{formatDigits(r.period)}</span> },
    { key: 'gross', header: amountHeader('gross'), align: 'end', width: '9rem', render: (r) => amount(r, 'grossRial') },
    { key: 'commission', header: amountHeader('commission'), align: 'end', width: '9rem', render: (r) => amount(r, 'commissionRial') },
    { key: 'refunds', header: amountHeader('refunds'), align: 'end', width: '9rem', render: (r) => amount(r, 'refundsRial') },
    { key: 'net', header: amountHeader('net'), align: 'end', width: '9rem', render: (r) => amount(r, 'netRial', 'font-semibold') },
    { key: 'status', header: t('marketplace.settlements.columns.status'), width: '8rem', render: (r) => <StatusChip value={r.status} prefix="marketplace.settlements.status" /> },
  ];

  return (
    <div data-testid="mk-settlements">
      <div className="mb-3 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3">
        <SegmentedControl
          semantics="filter"
          size="sm"
          ariaLabel={t('marketplace.settlements.filters.label')}
          options={SETTLEMENT_STATUSES.map((value) => ({ value, label: t(`marketplace.settlements.filters.${value || 'all'}`) }))}
          value={view.sstatus || ''}
          onChange={(sstatus) => go({ sstatus, settlement: '' })}
          data-testid="mk-settlements-filter"
        />
        <div className="w-44" data-testid="mk-settlements-period">
          <TextInput
            label={t('marketplace.settlements.period')}
            value={draft}
            onChange={(e) => setDraft(e.target?.value ?? e)}
            dir="ltr"
            maxLength={7}
            error={draftInvalid ? t('marketplace.settlements.periodInvalid') : undefined}
          />
        </div>
        {filtered ? (
          <Button variant="ghost" size="sm" onClick={clear} data-testid="mk-settlements-clear">
            <X size={14} aria-hidden />
            {t('common.actions.clearFilters')}
          </Button>
        ) : null}
      </div>

      <div className="rounded-lg border border-border bg-card transition-opacity" style={{ opacity: list.refreshing ? 0.45 : 1 }} aria-busy={list.refreshing || undefined}>
        {list.loading ? (
          <TableSkeleton cols={7} label={t('marketplace.settlements.loading')} data-testid="mk-settlements-loading" />
        ) : list.error ? (
          <ErrorState
            title={t('marketplace.settlements.error')}
            description={list.error}
            onRetry={list.retry}
            retryLabel={t('common.actions.retry')}
            data-testid="mk-settlements-error"
          />
        ) : (
          <div className="overflow-x-auto">
            <Table
              dense
              ariaLabel={t('marketplace.settlements.tableAria')}
              columns={columns}
              rows={list.rows}
              rowKey={(r) => r.id}
              onRowActivate={(r) => go({ settlement: r.id })}
              rowProps={(r) => ({
                'data-testid': `mk-settlements-row-${r.id}`,
                'aria-label': t('marketplace.settlements.open', { vendor: r.vendorName || t('marketplace.listings.unknownVendor'), period: formatDigits(r.period) }),
              })}
              empty={filtered ? (
                <EmptyState
                  icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
                  title={t('marketplace.settlements.emptyFiltered.title')}
                  description={t('marketplace.settlements.emptyFiltered.desc')}
                  action={<Button variant="outline" onClick={clear}>{t('common.actions.clearFilters')}</Button>}
                  data-testid="mk-settlements-empty"
                />
              ) : (
                <EmptyState
                  icon={<Banknote size={44} strokeWidth={1.5} aria-hidden />}
                  title={t('marketplace.settlements.empty.title')}
                  description={t('marketplace.settlements.empty.desc')}
                  data-testid="mk-settlements-empty"
                />
              )}
              data-testid="mk-settlements-table"
            />
            {list.cursor ? <LoadMore onClick={list.more} pending={list.moreLoading} data-testid="mk-settlements-more" /> : null}
          </div>
        )}
      </div>

      {view.settlement ? (
        <SettlementDrawer
          settlement={selected}
          onClose={() => go({ settlement: '' })}
          onChanged={(next) => { list.replace((r) => r.id === next.id, next); list.reload(); }}
        />
      ) : null}
    </div>
  );
}

function SettlementDrawer({ settlement, onClose, onChanged }) {
  const { t, formatDate, formatDigits } = useTranslation();
  const lineKind = useEnumLabel('marketplace.settlements.drawer.lineKind');
  const [action, setAction] = React.useState(null);
  const [error, setError] = React.useState(null);

  if (!settlement) {
    return (
      <MarketDrawer onClose={onClose} title={t('marketplace.settlements.drawer.title')} data-testid="mk-settlement-drawer">
        <NotInList data-testid="mk-settlement-drawer-missing" />
      </MarketDrawer>
    );
  }

  const vendor = settlement.vendorName || t('marketplace.listings.unknownVendor');
  const period = formatDigits(settlement.period);
  // One settlement is one store's month: one currency. The «(تومان)» labels
  // are right only for IRR; any other currency's figures carry their own code.
  const { currency } = settlement;
  const amountLabel = (key) => (isIrr(currency)
    ? t(`marketplace.settlements.columns.${key}`)
    : t(`marketplace.settlements.columnsNoUnit.${key}`));
  const open = (kind) => {
    setError(null);
    setAction(kind === 'close' ? {
      key: 'close',
      title: t('marketplace.settlements.confirm.close', { vendor, period }),
      body: t('marketplace.settlements.confirm.closeBody'),
      confirmLabel: t('marketplace.settlements.actions.close'),
      field: null,
      document: CLOSE_SETTLEMENT,
      variables: () => ({ id: settlement.id }),
      success: t('marketplace.settlements.success.close'),
    } : {
      key: 'markPaid',
      title: t('marketplace.settlements.confirm.markPaid', { vendor, period }),
      body: t('marketplace.settlements.confirm.markPaidBody'),
      confirmLabel: t('marketplace.settlements.actions.markPaid'),
      field: 'reference',
      document: MARK_SETTLEMENT_PAID,
      variables: (reference) => ({ id: settlement.id, reference }),
      success: t('marketplace.settlements.success.markPaid'),
    });
  };

  const lineColumns = [
    { key: 'order', header: t('marketplace.settlements.drawer.lineColumns.order'), render: (l) => <Code>{l.orderNumber}</Code> },
    { key: 'kind', header: t('marketplace.settlements.drawer.lineColumns.kind'), render: (l) => lineKind(l.kind) },
    { key: 'at', header: t('marketplace.settlements.drawer.lineColumns.at'), render: (l) => formatDate(l.at) },
    { key: 'gross', header: t('marketplace.settlements.drawer.lineColumns.gross'), align: 'end', render: (l) => <MoneyCell rials={l.grossRial} currency={currency} /> },
    { key: 'commission', header: t('marketplace.settlements.drawer.lineColumns.commission'), align: 'end', render: (l) => <MoneyCell rials={l.commissionRial} currency={currency} /> },
    { key: 'net', header: t('marketplace.settlements.drawer.lineColumns.net'), align: 'end', render: (l) => <MoneyCell rials={l.netRial} currency={currency} /> },
  ];

  return (
    <MarketDrawer
      onClose={onClose}
      title={t('marketplace.settlements.drawer.title')}
      sub={<span className="tabular-nums">{period}</span>}
      data-testid="mk-settlement-drawer"
    >
      <div className="flex flex-col gap-5">
        {error ? <Alert variant="error" data-testid="mk-settlement-drawer-error">{t('marketplace.drawer.failed', { message: error })}</Alert> : null}

        <div className="flex flex-wrap items-center gap-2">
          <span dir="auto" className="text-base font-bold text-foreground">{vendor}</span>
          <StatusChip value={settlement.status} prefix="marketplace.settlements.status" data-testid="mk-settlement-drawer-status" />
        </div>

        <section>
          <h2 className="mb-2 text-sm font-bold text-foreground">{t('marketplace.drawer.actions')}</h2>
          {settlement.status === 'open' ? (
            <Button onClick={() => open('close')} data-testid="mk-settlement-close">{t('marketplace.settlements.actions.close')}</Button>
          ) : settlement.status === 'closed' ? (
            <Button onClick={() => open('markPaid')} data-testid="mk-settlement-mark-paid">{t('marketplace.settlements.actions.markPaid')}</Button>
          ) : (
            <p className="text-sm text-muted-foreground">{t('marketplace.drawer.noActions')}</p>
          )}
        </section>

        <dl className="grid grid-cols-2 gap-4 text-sm">
          <Fact label={amountLabel('gross')} value={<MoneyCell rials={settlement.grossRial} currency={currency} />} />
          <Fact label={amountLabel('commission')} value={<MoneyCell rials={settlement.commissionRial} currency={currency} />} />
          <Fact label={amountLabel('refunds')} value={<MoneyCell rials={settlement.refundsRial} currency={currency} />} />
          <Fact label={amountLabel('net')} value={<MoneyCell rials={settlement.netRial} currency={currency} />} />
          <Fact label={t('marketplace.settlements.drawer.paidAt')} value={settlement.paidAt ? formatDate(settlement.paidAt) : null} />
          <Fact label={t('marketplace.settlements.drawer.reference')} value={settlement.paidReference ? <Code>{settlement.paidReference}</Code> : null} />
          <Fact label={t('marketplace.settlements.drawer.payout')} value={settlement.payoutAccountId ? <Code>{settlement.payoutAccountId}</Code> : null} />
          <Fact
            label={t('marketplace.settlements.drawer.income')}
            value={settlement.incomePosted ? t('marketplace.settlements.drawer.incomePosted') : t('marketplace.settlements.drawer.incomePending')}
          />
        </dl>

        <section>
          <h2 className="mb-2 text-sm font-bold text-foreground">{t('marketplace.settlements.drawer.lines')}</h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table
              dense
              ariaLabel={t('marketplace.settlements.drawer.linesAria')}
              columns={lineColumns}
              rows={settlement.lines || []}
              rowKey={(l) => l.id}
              empty={<EmptyState title={t('marketplace.settlements.drawer.noLines')} data-testid="mk-settlement-lines-empty" />}
              data-testid="mk-settlement-lines"
            />
          </div>
        </section>
      </div>

      <ActionConfirm
        action={action}
        testId="mk-settlement-dialog"
        onClose={() => setAction(null)}
        onError={setError}
        onDone={(d) => {
          setError(null);
          const next = d?.closeMarketplaceSettlement || d?.markMarketplaceSettlementPaid;
          if (next) onChanged(next);
        }}
      />
    </MarketDrawer>
  );
}
